<template>
  <v-app>
    <v-container fluid>

      <v-row>

        <!-- LEFT SIDE -->
        <v-col md="3" cols="12">

          <v-card class="pa-3">

            <v-card-title class="bg-blue-grey-lighten-4">
              Price Calculation Info
            </v-card-title>

            <v-card-text>

              <!-- PRICE INQUIRY -->
              <v-autocomplete
                label="Price Inquiry"
                v-model="price_inquiry_master_id"
                :items="getPriceInquiryList"
                item-title="code"
                item-value="id"
                density="compact"
                variant="outlined"
                @update:modelValue="loadInquiryDetails"
              />

              <!-- INQUIRY DETAILS -->
              <v-autocomplete
                class="mt-3"
                label="Inquiry Detail"
                v-model="price_inquiry_details_id"
                :items="inquiryDetails"
                :item-title="(item) => `${item.product?.code || ''} - ${item.product?.name || 'N/A'}`"
                item-value="id"
                density="compact"
                variant="outlined"
                @update:modelValue="selectDetail"
              />

              <v-divider class="my-3"></v-divider>

              <div v-if="selectedDetail">
                <p><b>Product:</b> {{ selectedDetail.product_name }}</p>
                <p><b>Net Weight:</b> {{ selectedDetail.net_weight }}</p>
                <p><b>Gross Weight:</b> {{ selectedDetail.gross_weight }}</p>
                <p><b>CBM:</b> {{ selectedDetail.cbm }}</p>
              </div>

            </v-card-text>

          </v-card>

        </v-col>

        <!-- RIGHT SIDE -->
        <v-col md="9" cols="12">

          <v-card class="pa-4">

            <v-card-title class="bg-blue-grey-lighten-4">
              Price Calculation Form
            </v-card-title>

            <v-card-text>

              <v-row>

                <v-col md="4">
                  <v-text-field
                    v-model="form.hs_code"
                    label="HS Code"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>

                <v-col md="4">
                  <v-text-field
                    v-model="form.assessment_value"
                    label="Assessment Value"
                    type="number"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>

                <v-col md="4">
                  <v-text-field
                    v-model="form.tti"
                    label="TTI"
                    type="number"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>

                <v-col md="4">
                  <v-text-field
                    v-model="form.shipping_cost"
                    label="Shipping Cost"
                    type="number"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>

                <v-col md="4">
                  <v-text-field
                    v-model="form.tax_per_kg"
                    label="Tax / KG"
                    type="number"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>

                <v-col md="4">
                  <v-text-field
                    v-model="form.total_tax"
                    label="Total Tax"
                    type="number"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>

                <v-col md="4">
                  <v-text-field
                    v-model="form.other_cost"
                    label="Other Cost"
                    type="number"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>

                <v-col md="4">
                  <v-text-field
                    v-model="form.profit"
                    label="Profit"
                    type="number"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>

                <v-col md="4">
                  <v-text-field
                    v-model="form.proposed_price"
                    label="Proposed Price"
                    type="number"
                    density="compact"
                    variant="outlined"
                  />
                </v-col>

              </v-row>

              <v-btn
                class="mt-4 bg-primary"
                :loading="loading"
                @click="submitData"
              >
                Save Calculation
              </v-btn>

            </v-card-text>

          </v-card>

        </v-col>

      </v-row>

    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const store = useStore();

const loading = ref(false);

const price_inquiry_master_id = ref(null);
const price_inquiry_details_id = ref(null);

const inquiryDetails = ref([]);
const selectedDetail = ref(null);

const form = ref({
  hs_code: "",
  assessment_value: 0,
  tti: 0,
  shipping_cost: 0,
  tax_per_kg: 0,
  total_tax: 0,
  other_cost: 0,
  profit: 0,
  proposed_price: 0,
});

const getPriceInquiryList = computed(() => {
  return store.getters.getPriceInquiryList || [];
});

const loadInquiryDetails = async () => {

  const inquiry = getPriceInquiryList.value.find(
    (x) => x.id == price_inquiry_master_id.value
  );

  inquiryDetails.value = inquiry?.details || [];

};

const selectDetail = () => {

  selectedDetail.value = inquiryDetails.value.find(
    (x) => x.id == price_inquiry_details_id.value
  );

  if (!selectedDetail.value) return;

  form.value = {
    hs_code: selectedDetail.value.priceCalculation?.hs_code || "",
    assessment_value: selectedDetail.value.priceCalculation?.assessment_value || 0,
    tti: selectedDetail.value.priceCalculation?.tti || 0,
    shipping_cost: selectedDetail.value.priceCalculation?.shipping_cost || 0,
    tax_per_kg: selectedDetail.value.priceCalculation?.tax_per_kg || 0,
    total_tax: selectedDetail.value.priceCalculation?.total_tax || 0,
    other_cost: selectedDetail.value.priceCalculation?.other_cost || 0,
    profit: selectedDetail.value.priceCalculation?.profit || 0,
    proposed_price: selectedDetail.value.priceCalculation?.proposed_price || 0,
  };

};

const submitData = async () => {

  loading.value = true;

  try {

    const payload = {
      price_inquiry_master_id: price_inquiry_master_id.value,
      price_inquiry_details_id: price_inquiry_details_id.value,
      ...form.value,
    };

    await store.dispatch("saveUpdatePriceCalculation", payload);

    toast.success("Saved successfully");

  } catch (error) {

    toast.error(error.response?.data?.message);

  } finally {

    loading.value = false;

  }

};

onMounted(async () => {

  await store.dispatch("getPriceInquiries", {});

});
</script>
